import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InfoIconWithTooltip } from "../../components/UIToolkit/molecules/InfoIconWithTooltip";
import type { CustomColumnNamesConfig, PrintoutColumn } from "../../downloads/dailyPrintout/types";
import { FONT_SIZE } from "../../theme/tokens";
import type { PrintoutColumnType } from "./Header/usePrintout";

const COLUMN_NAME_MAX_LENGTH = 50;

interface CustomColumnNamesSettingProps {
	isDetailed: boolean;
	columns: PrintoutColumn[];
	customColumnNames: CustomColumnNamesConfig;
	onCustomColumnNamesChange: (next: CustomColumnNamesConfig) => void;
	columnLabel: (type: PrintoutColumnType) => string;
}

/**
 * Detailed-only: a custom heading per selected column, as a compact two-column list (column → heading)
 * inside the Columns section. A blank heading keeps the column's own label, shown as the placeholder.
 */
export const CustomColumnNamesSetting: FC<CustomColumnNamesSettingProps> = ({
	isDetailed,
	columns,
	customColumnNames,
	onCustomColumnNamesChange,
	columnLabel,
}) => {
	const { t } = useTranslation();
	if (!isDetailed) return null;

	const customLabelFor = (type: PrintoutColumnType): string => {
		// DEFAULT: a selected column omitted from the sparse persisted map starts with an empty input;
		// leaving it empty is the explicit UI choice that keeps the original/dynamic printout heading.
		return customColumnNames.labels[type] ?? "";
	};
	const enabledColumns = columns.filter((column) => column.enabled);

	return (
		<Box data-testid="printout-custom-column-names-setting" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
				{/* Caption-sized and wrapping, like the column rows above it: at 14px on one line the label and
				    its ⓘ ran past a 280px panel. */}
				<Box sx={{ minWidth: 0 }}>
					<SystemSwitch
						dataTestId="printout-custom-column-names-switch"
						checked={customColumnNames.enabled}
						onChange={() => onCustomColumnNamesChange({ ...customColumnNames, enabled: !customColumnNames.enabled })}
						label={t("printout.custom.column.names")}
						labelFontSize={FONT_SIZE.CAPTION}
						labelWrap
					/>
				</Box>
				<InfoIconWithTooltip dataTestId="printout-custom-column-names-info" title={t("printout.custom.column.names.description")} />
			</Box>
			{customColumnNames.enabled && (
				<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)", alignItems: "center", columnGap: 1, rowGap: 0.75 }}>
					{enabledColumns.map((column) => (
						<Box key={column.type} sx={{ display: "contents" }}>
							<Typography
								sx={{
									minWidth: 0,
									fontSize: FONT_SIZE.CAPTION,
									fontWeight: 500,
									color: "primary.dark",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{columnLabel(column.type)}
							</Typography>
							<Box sx={{ minWidth: 0 }}>
								<SystemValidationTextField
									value={customLabelFor(column.type)}
									onChange={(event) =>
										onCustomColumnNamesChange({
											...customColumnNames,
											labels: { ...customColumnNames.labels, [column.type]: event.target.value },
										})
									}
									placeholder={columnLabel(column.type)}
									reserveHeightForHelperText={false}
									// The placeholder is the input's own text, so this sizes the typed heading and the placeholder together.
									inputSx={{ "& .MuiInputBase-root": { minWidth: 0 }, "& .MuiInputBase-input": { fontSize: FONT_SIZE.CAPTION, minWidth: 0 } }}
									formControlSx={{ minWidth: 0 }}
									dataTestId={`printout-custom-column-name-${column.type}-input`}
									inputProps={{
										inputProps: {
											maxLength: COLUMN_NAME_MAX_LENGTH,
											"aria-label": t("printout.custom.column.name.label", { column: columnLabel(column.type) }),
										},
									}}
								/>
							</Box>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};
